import React, { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Col, DatePicker, Form, message, Modal, Row } from "antd";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import SelectDropdown from "react-select";
import dayjs from "dayjs";
import TextInputField from "../../reusableField/TextInputField2";
import {
  validationRegexAlphaNumeric,
  validationRegexAmount,
  validationRegexOnlyAlpha,
  validationRegexOnlyAlpha6,
} from "../../regex/regex";
import {
  Add_Multiple_Users,
  Bulk_Upload,
  Bulk_Upload_Sheet,
  Current_Designation,
  Get_City,
  Get_States,
  Get_User_Types,
  GET_MANAGER_WITH_FULLNAME_EMPID_LOCATION,
  List_Employement_Type_From_Role,
  List_Gender_From_Role,
  Role_Account_Query,
} from "../../services/userAPI";
import BulkUploadFiles from "../../Images/bulk-upload-files -added-icon.png";
import "../Dashboard/Darts/masterConfiguration/userManagement/userManagement.css";

const requiredStarStyle = `
  .user-registration-modal .required-label::after {
    content: " *";
    color: #c81e1e;
    font-weight: bold;
    font-family: Inter;
  }
`;

const RoleAddUsersModal = ({ open, onClose, roleId, accountId, onSaved }) => {
  const [form] = Form.useForm();

  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const [loginConfig, setLoginConfig] = useState({ numberLogin: false, emailLogin: false });

  const { data: jobRoleData } = useQuery(Role_Account_Query, {
    variables: { accountId: accountId ? parseInt(accountId) : null },
    fetchPolicy: "network-only",
    skip: !accountId || !open,
  });
  const currentRole = jobRoleData?.getAllJobrole?.find((r) => r.id === parseInt(roleId) || r.id === roleId);

  useEffect(() => {
    const roleRow = currentRole || jobRoleData?.getAllJobrole?.[0];
    if (roleRow) {
      setLoginConfig({ numberLogin: roleRow.numberLogin, emailLogin: roleRow.emailLogin });
    }
  }, [jobRoleData]);

  const [formData, setFormData] = useState({
    fullTimeConsultant: "",
    currentBranch: "",
    employeeIdOfManager: "",
    nameOfManager: "",
    id: "",
    userId: null,
    dateOfJoining: null,
    currentDesignation: "",
    state: "",
    city: "",
    zone: "",
    dateOfReporting: null,
    employeeid: "",
    employeeMailId: "",
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: null,
    highestEducationQualification: "",
    previousEmployer: "",
    previousExperience: "",
    phoneNumber: "",
  });

  const [formDataList, setFormDataList] = useState([
    {
      fullTimeConsultant: null,
      dateOfJoining: null,
      currentDesignation: "",
      currentBranch: "",
      state: "",
      city: "",
      zone: "",
      employeeIdOfManager: "",
      dateOfReporting: null,
      nameOfManager: "",
    },
  ]);

  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [managerName, setManagerName] = useState("No Manager");
  const [selectedManagerEmployeeId, setSelectedManagerEmployeeId] = useState("");
  const [managerSearchTerm, setManagerSearchTerm] = useState("");

  const [addUsers, { loading: addUsersLoading }] = useMutation(Add_Multiple_Users);

  const { data: stateData, loading: stateLoading } = useQuery(Get_States, { skip: !open });
  const stateOptions =
    stateData?.getStates?.map((state) => ({ value: state.id, label: state.stateName })) || [];

  const [fetchCities, { loading: citiesLoading }] = useLazyQuery(Get_City, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setCityOptions(
        data?.getCity?.map((city) => ({ value: city.id, label: city.cityName })) || []
      );
    },
  });

  const { data: userTypesData } = useQuery(Get_User_Types, {
    fetchPolicy: "network-only",
    skip: !open,
  });
  const userTypesOptions =
    userTypesData?.getUserTypes?.map((type) => ({ value: type.id, label: type.userType })) || [];

  const { data: managersListData, loading: managersListLoading } = useQuery(
    GET_MANAGER_WITH_FULLNAME_EMPID_LOCATION,
    {
      variables: {
        roleId: parseInt(roleId) || 0,
        searchTerm: managerSearchTerm || "",
        pagingCriteria: { limit: 50, cursor: null, sortByField: "created_at", sortOrder: "desc" },
      },
      skip: !roleId || !open,
      fetchPolicy: "network-only",
    }
  );

  const managerOptions = useMemo(() => {
    if (!managersListData?.getManagerWithFullnameEmpidLocation?.data) return [];
    return managersListData.getManagerWithFullnameEmpidLocation.data.map((manager) => {
      const fullName = [manager.name, manager.surname].filter(Boolean).join(" ");
      const location = manager.location || manager.cityName || "";
      const labelParts = [fullName, manager.employeeId, location].filter(Boolean);
      return {
        value: manager.id,
        label: labelParts.join(" | "),
        employeeId: manager.employeeId,
        name: fullName,
        location,
      };
    });
  }, [managersListData]);

  const { data: genderData } = useQuery(List_Gender_From_Role, {
    variables: { jobroleId: parseInt(roleId) },
    skip: !roleId || !open,
  });
  const genderOptions =
    genderData?.listGenderFromRole?.map((g) => ({ value: g.id, label: g.name })) || [];

  const { data: employementData } = useQuery(List_Employement_Type_From_Role, {
    variables: { jobRoleId: parseInt(roleId) },
    skip: !roleId || !open,
  });
  const employementOptions =
    employementData?.listEmployementTypeFromRole?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || [];

  const { data: designationsData } = useQuery(Current_Designation, {
    variables: { jobroleId: parseInt(roleId) },
    skip: !roleId || !open,
  });
  const designationOptions =
    designationsData?.getDesignationsJobroleid?.map((type) => ({
      value: type.id,
      label: type.designation,
    })) || [];

  /* ------------------------- bulk upload ------------------------- */
  const [isModalVisibleForUpload, setIsModalVisibleForUpload] = useState(false);
  const handleCancelUpload = () => setIsModalVisibleForUpload(false);
  const [bulkUploadUsers] = useMutation(Bulk_Upload);
  const [file, setFile] = useState(null);

  const [getBulkUploadExcel, { loading: downloadLoading, data: downloadData, error: downloadError }] =
    useLazyQuery(Bulk_Upload_Sheet);

  const handleDownload = () => {
    getBulkUploadExcel({ variables: { roleId: Number(roleId) } });
  };

  useEffect(() => {
    if (downloadData && downloadData.getBulkUploadExcel) {
      const a = document.createElement("a");
      a.href = downloadData.getBulkUploadExcel;
      a.download = "Bulk_Upload_Sheet.xlsx";
      a.style.display = "none";
      document.body.appendChild(a);
      setTimeout(() => {
        a.click();
        document.body.removeChild(a);
      }, 100);
    }
    if (downloadError) {
      console.error("API Error:", downloadError);
    }
  }, [downloadData, downloadError]);

  const handleUpload = async () => {
    try {
      const response = await bulkUploadUsers({
        variables: { accountId: parseInt(accountId), roleId: parseInt(roleId), document: file },
      });
      const uploadResult = response?.data?.bulkUploadUsers;
      if (uploadResult?.success) {
        message.success("File uploaded successfully!");
      } else {
        message.error(uploadResult?.message || "File upload failed");
      }
      setFile(null);
      handleCancelUpload();
      onSaved?.();
    } catch (err) {
      console.error("Upload error:", err);
      message.error("File upload failed. Please try again.");
    }
  };

  /* --------------------------- handlers --------------------------- */
  const handleCancel = (e) => {
    if (e) e.preventDefault();
    setStep(0);
    form.resetFields();
    setFormData({
      fullTimeConsultant: "",
      currentBranch: "",
      employeeIdOfManager: "",
      nameOfManager: "",
      id: "",
      userId: null,
      dateOfJoining: null,
      currentDesignation: "",
      state: "",
      city: "",
      zone: "",
      dateOfReporting: null,
      employeeid: "",
      employeeMailId: "",
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: null,
      highestEducationQualification: "",
      previousEmployer: "",
      previousExperience: "",
      phoneNumber: "",
    });
    setSelectedState(null);
    setSelectedCity(null);
    setManagerName("No Manager");
    setFormDataList([
      {
        fullTimeConsultant: "",
        dateOfJoining: null,
        currentDesignation: "",
        currentBranch: "",
        state: "",
        city: "",
        zone: "",
        employeeIdOfManager: "",
        dateOfReporting: null,
        nameOfManager: "No Manager",
      },
    ]);
    setTouched({});
    setErrors({});
    onClose?.();
  };

  const handleInputChange = (e, field, index) => {
    const { name, value } = e.target || e;
    const fieldName = field || name;
    const updatedValue =
      fieldName === "previousExperience" && value !== "" ? parseInt(value, 10) : value;

    if (index !== undefined) {
      setFormDataList((prevList) =>
        prevList.map((item, idx) => (idx === index ? { ...item, [fieldName]: updatedValue } : item))
      );
    } else {
      setFormData((prev) => ({ ...prev, [fieldName]: updatedValue }));
    }

    setTouched((prev) => ({
      ...prev,
      [index !== undefined ? `${index}-${fieldName}` : fieldName]: true,
    }));

    if (fieldName === "employeeMailId") {
      if (!value.trim()) {
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      } else if (!emailRegex.test(value)) {
        setErrors((prev) => ({ ...prev, [fieldName]: "Invalid email format" }));
      } else {
        setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
      }
    } else {
      if (index !== undefined) {
        setErrors((prev) => ({ ...prev, [`${index}-${fieldName}`]: undefined }));
      } else {
        setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
      }
    }
  };

  const removeUserFields = (index) => {
    setFormDataList(formDataList.filter((_, i) => i !== index));
  };

  const handleDateChange = (name, date) => {
    const normalDate = date ? date.format("YYYY-MM-DD") : null;
    setFormData((prevData) => ({ ...prevData, [name]: normalDate }));
    setTouched((prevTouched) => ({ ...prevTouched, [name]: true }));
    if (normalDate) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    }
  };

  const handleListDateChange = (index, field, date) => {
    setFormDataList((prevList) =>
      prevList.map((item, idx) => (idx === index ? { ...item, [field]: date } : item))
    );
  };

  const handleDesignationChange = (selectedOption) => {
    setSelectedDesignation(selectedOption);
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.currentDesignation;
      return newErrors;
    });
    setTouched((prevTouched) => ({ ...prevTouched, currentDesignation: true }));
  };

  const zoneOptions = [
    { label: "North", value: "north" },
    { label: "South", value: "south" },
    { label: "East", value: "east" },
    { label: "West", value: "west" },
  ];

  const handleZoneChange = (selectedOption, index) => {
    setFormDataList((prevList) =>
      prevList.map((item, idx) => (idx === index ? { ...item, zone: selectedOption } : item))
    );
    form.setFieldsValue({
      [index]: { ...form.getFieldValue(index), zone: selectedOption || "" },
    });
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[`${index}-zone`];
      return newErrors;
    });
    setTouched((prevTouched) => ({ ...prevTouched, [`${index}-zone`]: true }));
  };

  const handleUserTypeChange = (selectedOption, index) => {
    setFormDataList((prevList) =>
      prevList.map((item, idx) => (idx === index ? { ...item, userType: selectedOption.value } : item))
    );
    form.setFieldsValue({
      [index]: { ...form.getFieldValue(index), userType: selectedOption || "" },
    });
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[`${index}-userType`];
      return newErrors;
    });
    setTouched((prevTouched) => ({ ...prevTouched, [`${index}-userType`]: true }));
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setSelectedCity(null);
    setCityOptions([]);
    if (selectedOption?.value) {
      fetchCities({ variables: { stateId: parseInt(selectedOption.value, 10) } });
    }
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.state;
      return newErrors;
    });
  };

  const handleCityChange = (selectedOption) => {
    setSelectedCity(selectedOption);
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.city;
      return newErrors;
    });
  };

  const handleManagerSelect = (selectedOption, index) => {
    if (selectedOption) {
      setSelectedManagerEmployeeId(selectedOption.employeeId || "");
      setManagerName(selectedOption.name || "No Manager");
      const updatedFormDataList = [...formDataList];
      updatedFormDataList[index] = {
        ...updatedFormDataList[index],
        employeeIdOfManager: selectedOption.employeeId,
      };
      setFormDataList(updatedFormDataList);
    } else {
      setSelectedManagerEmployeeId("");
      setManagerName("No Manager");
      const updatedFormDataList = [...formDataList];
      updatedFormDataList[index] = { ...updatedFormDataList[index], employeeIdOfManager: null };
      setFormDataList(updatedFormDataList);
    }
  };

  const validateFirstStep = (data) => {
    const errs = {};
    if (!data.employeeid?.trim()) errs.employeeid = "Employee ID is required";
    if (loginConfig.emailLogin && !loginConfig.numberLogin) {
      if (!data.employeeMailId?.trim()) {
        errs.employeeMailId = "Employee Mail ID is required";
      } else if (!emailRegex.test(data.employeeMailId)) {
        errs.employeeMailId = "Invalid email format";
      }
    }
    if (loginConfig.numberLogin && !loginConfig.emailLogin) {
      if (!data.phoneNumber?.trim()) {
        errs.phoneNumber = "Phone number is required";
      } else if (!/^[6-9]\d{9}$/.test(data.phoneNumber.trim())) {
        errs.phoneNumber = "Invalid phone number format";
      }
    } else if (data.phoneNumber?.trim() && data.phoneNumber.trim().length !== 10) {
      errs.phoneNumber = "Phone number must be exactly 10 digits";
    }
    if (!data.firstName?.trim()) errs.firstName = "First name is required";
    if (!data.lastName?.trim()) errs.lastName = "Last name is required";
    if (!data.dateOfBirth) errs.dateOfBirth = "Date of birth is required";
    if (!data.gender) errs.gender = "Gender is required";
    return errs;
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newTouched = { ...touched };
    const fieldsToValidate = [
      "employeeid",
      "firstName",
      "lastName",
      "gender",
      "dateOfBirth",
      "highestEducationQualification",
      "previousEmployer",
      "previousExperience",
    ];
    if (loginConfig.emailLogin && !loginConfig.numberLogin) fieldsToValidate.push("employeeMailId");
    if (loginConfig.numberLogin && !loginConfig.emailLogin) fieldsToValidate.push("phoneNumber");
    fieldsToValidate.forEach((field) => (newTouched[field] = true));
    setTouched(newTouched);
    const stepErrors = validateFirstStep(formData);
    setErrors((prevErrors) => ({ ...prevErrors, ...stepErrors }));
    if (Object.keys(stepErrors).length === 0) {
      setStep(1);
    }
  };

  const handleSubmit = async () => {
    const formatDate = (dateValue) => {
      if (!dateValue) return null;
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error("Invalid date format:", dateValue);
        return null;
      }
    };

    localStorage.setItem("roleId", roleId);
    localStorage.setItem("accountId", accountId);

    const userData = formDataList.map((item) => {
      const selectedGender = genderOptions.find(
        (option) => option.value === (formData.gender || item.gender)
      );
      const selectedEmployementTypeId = employementOptions.find(
        (option) => option.value === (formData.fullTimeConsultant || item.fullTimeConsultant)
      );
      const selectedUserType = userTypesOptions.find(
        (option) => option.value === (formData.userType || item.userType)
      );

      return {
        accountId: parseInt(accountId) || null,
        currentRoleId: parseInt(roleId) || null,
        employeeId: formData.employeeid || item.employeeid || "",
        firstName: formData.firstName || item.firstName || "",
        lastName: formData.lastName || item.lastName || "",
        email: formData.employeeMailId || item.employeeMailId || "",
        phoneNumber: formData.phoneNumber || item.phoneNumber || "",
        genderId: selectedGender ? selectedGender.value : null,
        employeeTypeId: selectedEmployementTypeId ? selectedEmployementTypeId.value : null,
        dateOfBirth:
          formData.dateOfBirth || item.dateOfBirth
            ? formatDate(formData.dateOfBirth || item.dateOfBirth)
            : "",
        joiningDate:
          formData.dateOfJoining || item.dateOfJoining
            ? formatDate(formData.dateOfJoining || item.dateOfJoining)
            : "",
        validFrom:
          formData.dateOfReporting || item.dateOfReporting
            ? formatDate(formData.dateOfReporting || item.dateOfReporting)
            : null,
        higestEducationQualification:
          formData.highestEducationQualification || item.highestEducationQualification || "",
        previousEmployer: formData.previousEmployer || item.previousEmployer || "",
        managerEmployeeId: formData.employeeIdOfManager || item.employeeIdOfManager || null,
        designation: selectedDesignation?.value ? selectedDesignation.value : "Unknown",
        state: selectedState?.value ? selectedState.value : "Unknown",
        city: selectedCity?.value ? selectedCity.value : "Unknown",
        zone: item.zone?.value || item.zone || "Unknown",
        location: item.location || "Unknown",
        userId: null,
        branch: item.currentBranch || "Unknown",
        previousExperience:
          formData.previousExperience || item.previousExperience
            ? parseInt(formData.previousExperience || item.previousExperience, 10)
            : 0,
        userTypeId: selectedUserType?.value ? selectedUserType.value : null,
      };
    });

    try {
      const response = await addUsers({ variables: { data: userData } });
      const result = response?.data?.addMultipleUsers;
      const success = result?.success;
      const userErrors = result?.data || [];

      if (success) {
        message.success("Users added successfully!");
        handleCancel(false);
        form.resetFields();
        onSaved?.();
      } else {
        userErrors.forEach((error) => {
          if (error?.message) {
            message.error(error.message);
          }
        });
        console.error("User addition failed:", response.data);
      }
    } catch (error) {
      console.error("GraphQL Error:", error);
      message.error("Failed to add users due to a system error.");
    }
  };

  /* ----------------------------- render ----------------------------- */
  return (
    <>
      <style>{requiredStarStyle}</style>
      <Modal
        title="User Registration Form"
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={640}
        centered
        className="user-registration-modal"
      >
        <Breadcrumb separator=">">
          <Breadcrumb.Item>
            <a
              style={{
                fontWeight: step === 0 ? "bold" : "normal",
                color: step === 0 ? "#AD0101" : "rgb(188, 188, 188)",
              }}
              onClick={() => setStep(0)}
            >
              1. User Basic Data
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <a
              style={{
                fontWeight: step === 1 ? "bold" : "normal",
                color: step === 1 ? "#AD0101" : "rgb(188, 188, 188)",
              }}
            >
              2. User Work Data
            </a>
          </Breadcrumb.Item>
        </Breadcrumb>

        <Form
          form={form}
          layout="vertical"
          className="user-registration-form"
          onSubmit={(e) => e.preventDefault()}
        >
          {step === 0 ? (
            <>
              <Row gutter={[16, 16]} className="registration-form-row">
                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">Employee ID</label>
                  <TextInputField
                    name="employeeid"
                    value={formData.employeeid || ""}
                    placeholder="Employee ID"
                    feedbackMessage="Please input the current branch."
                    validationRegex={validationRegexAlphaNumeric}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleNext(e)}
                  />
                  {touched.employeeid && errors.employeeid && (
                    <div className="createClientErrors">{errors.employeeid}</div>
                  )}
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className={loginConfig.emailLogin && !loginConfig.numberLogin ? "required-label" : ""}>
                    Employee Mail ID
                  </label>
                  <TextInputField
                    name="employeeMailId"
                    value={formData.employeeMailId}
                    placeholder="Employee Mail ID"
                    feedbackMessage="Please enter a valid email address."
                    onChange={handleInputChange}
                  />
                  {touched.employeeMailId && errors.employeeMailId && (
                    <div className="createClientErrors">{errors.employeeMailId}</div>
                  )}
                </Col>
              </Row>

              <Row gutter={[16, 16]} className="registration-form-row">
                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">First Name</label>
                  <TextInputField
                    name="firstName"
                    value={formData.firstName}
                    placeholder="First Name"
                    feedbackMessage="Please input the current branch."
                    validationRegex={validationRegexOnlyAlpha}
                    onChange={handleInputChange}
                  />
                  {touched.firstName && errors.firstName && (
                    <div className="createClientErrors">{errors.firstName}</div>
                  )}
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">Last Name</label>
                  <TextInputField
                    name="lastName"
                    value={formData.lastName}
                    placeholder="Last Name"
                    feedbackMessage="Please input the current branch."
                    validationRegex={validationRegexOnlyAlpha}
                    onChange={handleInputChange}
                  />
                  {touched.lastName && errors.lastName && (
                    <div className="createClientErrors">{errors.lastName}</div>
                  )}
                </Col>
              </Row>

              <Row gutter={[16, 16]} className="registration-gender-dob-row">
                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">Gender</label>
                  <SelectDropdown
                    options={genderOptions}
                    classNamePrefix="registration-select"
                    styles={{ container: (provided) => ({ ...provided, width: "100%" }) }}
                    placeholder="Select Gender"
                    value={genderOptions.find((option) => option.value === formData.gender) || null}
                    onChange={(selectedOption) =>
                      handleInputChange({ target: { name: "gender", value: selectedOption.value } })
                    }
                    isSearchable
                  />
                  {touched.gender && errors.gender && (
                    <div className="createClientErrors">{errors.gender}</div>
                  )}
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">Date of Birth</label>
                  <DatePicker
                    className="registration-date-picker"
                    style={{ width: "100%" }}
                    placeholder="Date of Birth"
                    value={formData.dateOfBirth ? dayjs(formData.dateOfBirth, "YYYY-MM-DD") : null}
                    onChange={(date) => handleDateChange("dateOfBirth", date)}
                    defaultPickerValue={dayjs().subtract(18, "year")}
                    disabledDate={(current) =>
                      current && current > dayjs().subtract(18, "year").endOf("day")
                    }
                  />
                  {touched.dateOfBirth && errors.dateOfBirth && (
                    <div className="createClientErrors">{errors.dateOfBirth}</div>
                  )}
                </Col>
              </Row>

              <Row gutter={[16, 16]} className="registration-form-row">
                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">Highest Education Qualification</label>
                  <TextInputField
                    name="highestEducationQualification"
                    value={formData.highestEducationQualification}
                    placeholder="Highest Education Qualification"
                    feedbackMessage="Please input the current branch."
                    validationRegex={validationRegexOnlyAlpha6}
                    onChange={handleInputChange}
                  />
                  {touched.highestEducationQualification && errors.highestEducationQualification && (
                    <div className="createClientErrors">{errors.highestEducationQualification}</div>
                  )}
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">Previous Employer</label>
                  <TextInputField
                    name="previousEmployer"
                    value={formData.previousEmployer}
                    placeholder="Previous Employer"
                    feedbackMessage="Please input the current branch."
                    validationRegex={validationRegexAlphaNumeric}
                    onChange={handleInputChange}
                  />
                  {touched.previousEmployer && errors.previousEmployer && (
                    <div className="createClientErrors">{errors.previousEmployer}</div>
                  )}
                </Col>
              </Row>

              <Row gutter={[16, 16]} className="registration-form-row">
                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className="required-label">Previous Experience (Months)</label>
                  <TextInputField
                    name="previousExperience"
                    value={formData.previousExperience}
                    placeholder="Previous Experience (Months)"
                    feedbackMessage="Please input the current branch."
                    validationRegex={validationRegexAmount}
                    onChange={(e) => handleInputChange(e, "previousExperience")}
                  />
                  {touched.previousExperience && errors.previousExperience && (
                    <div className="createClientErrors">{errors.previousExperience}</div>
                  )}
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <label className={loginConfig.numberLogin && !loginConfig.emailLogin ? "required-label" : ""}>
                    Phone Number
                  </label>
                  <TextInputField
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    placeholder="Phone Number"
                    maxLength={10}
                    onChange={(value) => {
                      const phoneValue = value?.target ? value.target.value : value || "";
                      if (!/^\d*$/.test(phoneValue)) return;
                      if (phoneValue.length > 0 && !/^[6-9]/.test(phoneValue)) return;
                      if (phoneValue.length > 10) return;
                      handleInputChange(
                        value?.target
                          ? value
                          : { target: { name: "phoneNumber", value: phoneValue } }
                      );
                    }}
                  />
                  {touched.phoneNumber && errors.phoneNumber && (
                    <div className="createClientErrors">{errors.phoneNumber}</div>
                  )}
                </Col>
              </Row>

              <div
                className="registration-basic-actions"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", width: "100%" }}
              >
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    type="button"
                    className="global-admin-bulk-upload-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsModalVisibleForUpload(true);
                    }}
                  >
                    Bulk Upload
                  </button>
                  <button
                    type="button"
                    className="handleDownload"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownload();
                    }}
                    disabled={downloadLoading}
                  >
                    Bulk Upload Sheet
                  </button>
                </div>
                <div>
                  <button className="nextButton" onClick={(e) => handleNext(e)}>
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <Form
                form={form}
                className="user-work-data-form"
                layout="vertical"
                onFinish={handleSubmit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              >
                {formDataList?.map((workData, index) => (
                  <div key={index} className="registration-user-record">
                    <Row gutter={[16, 16]} className="registration-form-row">
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label>
                          Employment Type
                          <span style={{ color: "#C21A17", marginLeft: "3px" }}>*</span>
                        </label>
                        <Form.Item
                          name={["formDataList", index, "fullTimeConsultant"]}
                          rules={[{ required: true, message: "This field is required!" }]}
                        >
                          <SelectDropdown
                            classNamePrefix="work-data-select"
                            options={employementOptions}
                            placeholder="Select Employment Type"
                            value={formDataList[index]?.fullTimeConsultant || null}
                            onChange={(selectedOption) => {
                              const updatedList = [...formDataList];
                              updatedList[index] = {
                                ...updatedList[index],
                                fullTimeConsultant: selectedOption.value,
                              };
                              setFormDataList(updatedList);
                              form.setFieldsValue({ fullTimeConsultant: selectedOption.value });
                            }}
                            isSearchable
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label>
                          Date of Joining
                          <span style={{ color: "#C21A17", marginLeft: "3px" }}>*</span>
                        </label>
                        <Form.Item
                          name={["formDataList", index, "dateOfJoining"]}
                          rules={[{ required: true, message: "Date of Joining is required!" }]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Date of Joining"
                            disabledDate={(current) => current && current > dayjs().endOf("day")}
                            onChange={(date) => handleListDateChange(index, "dateOfJoining", date)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="registration-form-row">
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label className="required-label">Current Designation</label>
                        <Form.Item
                          name={["currentDesignation"]}
                          rules={[{ required: true, message: "Designation is required!" }]}
                        >
                          <SelectDropdown
                            classNamePrefix="work-data-select"
                            options={designationOptions}
                            placeholder="Current Designation"
                            onChange={handleDesignationChange}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label className="required-label">Current Branch</label>
                        <Form.Item
                          name={["formDataList", index, "currentBranch"]}
                          rules={[
                            { required: true, message: "Branch is required!" },
                            { pattern: validationRegexOnlyAlpha, message: "Only alphabets are allowed!" },
                          ]}
                        >
                          <TextInputField
                            placeholder="Current Branch"
                            feedbackMessage="Please input the current branch."
                            onChange={(e) => handleInputChange(e, "currentBranch", index)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="registration-form-row">
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label className="required-label">State</label>
                        <Form.Item
                          name={["formDataList", index, "state"]}
                          rules={[{ required: true, message: "State is required!" }]}
                        >
                          <SelectDropdown
                            classNamePrefix="work-data-select"
                            options={stateOptions}
                            placeholder="Select State"
                            isLoading={stateLoading}
                            onChange={handleStateChange}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label className="required-label">City</label>
                        <Form.Item
                          name={["formDataList", index, "city"]}
                          rules={[{ required: true, message: "City is required!" }]}
                        >
                          <SelectDropdown
                            classNamePrefix="work-data-select"
                            options={cityOptions}
                            placeholder="Select City"
                            isDisabled={!selectedState}
                            isLoading={citiesLoading}
                            onChange={handleCityChange}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="registration-form-row">
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label className="required-label">Zone</label>
                        <Form.Item
                          name={["formDataList", index, "zone"]}
                          rules={[{ required: true, message: "Zone is required" }]}
                        >
                          <SelectDropdown
                            classNamePrefix="work-data-select"
                            options={zoneOptions}
                            placeholder="Zone"
                            onChange={(selectedOption) => handleZoneChange(selectedOption, index)}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label>Select Manager</label>
                        <Form.Item name={["formDataList", index, "employeeIdOfManager"]}>
                          <SelectDropdown
                            classNamePrefix="work-data-select"
                            options={managerOptions}
                            placeholder="Select Manager"
                            onChange={(selectedOption) => handleManagerSelect(selectedOption, index)}
                            isSearchable
                            isClearable
                            isLoading={managersListLoading}
                            onInputChange={(inputValue) => setManagerSearchTerm(inputValue)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="registration-form-row">
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label className={formDataList[index]?.employeeIdOfManager ? "required-label" : ""}>
                          Date of Reporting
                        </label>
                        <Form.Item
                          name={["formDataList", index, "dateOfReporting"]}
                          dependencies={[["formDataList", index, "employeeIdOfManager"]]}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const managerId = getFieldValue([
                                  "formDataList",
                                  index,
                                  "employeeIdOfManager",
                                ]);
                                if (managerId && !value) {
                                  return Promise.reject(
                                    new Error("Date of Reporting is required when Manager is selected!")
                                  );
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Date of Reporting"
                            onChange={(date) => handleListDateChange(index, "dateOfReporting", date)}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label>Employee ID of Manager</label>
                        <Form.Item>
                          <TextInputField
                            placeholder="Employee ID of Manager"
                            value={selectedManagerEmployeeId || "No Manager Selected"}
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="registration-form-row">
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <label className="required-label">User Type</label>
                        <Form.Item
                          name={["formDataList", index, "userType"]}
                          rules={[{ required: true, message: "User Type is required!" }]}
                        >
                          <SelectDropdown
                            classNamePrefix="work-data-select"
                            options={userTypesOptions}
                            placeholder="Select User Type"
                            value={
                              userTypesOptions.find(
                                (option) => option.value === formDataList[index]?.userType
                              ) || null
                            }
                            onChange={(selectedOption) => handleUserTypeChange(selectedOption, index)}
                            isSearchable
                          />
                        </Form.Item>
                        {touched[`${index}-userType`] && errors[`${index}-userType`] && (
                          <div className="createClientErrors">{errors[`${index}-userType`]}</div>
                        )}
                      </Col>
                    </Row>

                    {index > 0 && (
                      <button
                        type="button"
                        className="user-management-cancel-button"
                        onClick={() => removeUserFields(index)}
                        style={{
                          marginTop: "10px",
                          backgroundColor: "#AD0101",
                          color: "white",
                          display: "flex",
                          justifySelf: "end",
                          alignItems: "center",
                        }}
                      >
                        Remove User
                      </button>
                    )}
                  </div>
                ))}

                <div className="global-admin-modal-footer-user-management">
                  <button
                    type="button"
                    className="user-management-cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="user-management-save-button"
                    onClick={(e) => {
                      e.preventDefault();
                      form
                        .validateFields()
                        .then(() => {
                          handleSubmit();
                        })
                        .catch((errorInfo) => {
                          console.log("Validation Failed:", errorInfo);
                        });
                    }}
                    disabled={addUsersLoading}
                    style={{
                      opacity: addUsersLoading ? 0.7 : 1,
                      cursor: addUsersLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    {addUsersLoading ? (
                      <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </Form>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title="Bulk Lead Upload"
        open={isModalVisibleForUpload}
        onCancel={handleCancelUpload}
        footer={null}
        width={600}
        centered
        destroyOnClose
      >
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            border: "2px dashed #ccc",
            borderRadius: "8px",
            cursor: "pointer",
          }}
          onClick={() => document.getElementById("fileUpload").click()}
        >
          <div className="uploadIconOfAddUser">
            <img src={BulkUploadFiles} alt="Upload Icon" />
          </div>
          <p style={{ fontSize: "16px", fontFamily: "Inter" }}>
            Drag & Drop files or <span style={{ color: "#AD0101" }}>Browse</span>
          </p>
          <p style={{ color: "#666", fontSize: "12px", marginTop: "10px" }}>
            Supported formats: xlsx
          </p>
          <input
            type="file"
            id="fileUpload"
            style={{ display: "none" }}
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files[0])}
          />
        </div>

        {file && (
          <p style={{ textAlign: "center", marginTop: "10px", fontSize: "14px" }}>
            File Selected: {file.name}
          </p>
        )}

        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button
            onClick={handleUpload}
            disabled={!file}
            style={{
              background: "#FAC9CA",
              color: "#9B9C9C",
              border: "none",
              padding: "8px 20px",
              borderRadius: "4px",
              transition: "background 0.3s ease-in-out",
              cursor: file ? "pointer" : "not-allowed",
            }}
          >
            Upload
          </button>
        </div>
      </Modal>
    </>
  );
};

export default RoleAddUsersModal;
